// GtEnrich — AI enrichment on the platform, in three pieces used wherever people are:
//   GtEnrichModal     start a run for chosen people (or an import), with the cost first
//   GtEnrichProgress  a banner that follows runs to the end
//   GtEnrichCard      the record's enrichment card: sources, what was filled, and
//                     suggestions to accept or dismiss
// Enrichment never contacts anyone and never overwrites what a person entered.
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import API_BASE from '../config';

const json = { 'Content-Type': 'application/json' };
const KIND_ORDER = ['CV', 'LinkedIn', 'GitHub', 'Publication', 'Web'];

function ukDate(d) {
  if (!d) return '';
  const x = new Date(d);
  if (isNaN(x.getTime())) return '';
  return String(x.getDate()).padStart(2, '0') + '/' + String(x.getMonth() + 1).padStart(2, '0') + '/' + x.getFullYear();
}
// A signed-in fetch that returns parsed JSON and throws with the server's
// message on any error, so every screen behaves the same with these pieces.
export function useEnrichFetch(getToken) {
  return useMemo(() => async (path, opts = {}) => {
    const token = await getToken();
    const res = await fetch(API_BASE + path, { ...opts, headers: { ...(opts.headers || {}), Authorization: 'Bearer ' + token } });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(body.message || body.error || ('request failed: ' + res.status));
      err.status = res.status;
      throw err;
    }
    return body;
  }, [getToken]);
}

function money(n) { return '£' + Number(n || 0).toFixed(2); }
function plural(n, one, many) { return n + ' ' + (n === 1 ? one : many); }

// ── Start a run ──────────────────────────────────────────────────────────
export function GtEnrichModal({ candidateIds, importId, authFetch, flash, onClose, onStarted, allowDeep = true }) {
  const [est, setEst] = useState(null);
  const [deep, setDeep] = useState(false);
  // One person chosen on purpose is looked up again even if done recently.
  const [includeRecent, setIncludeRecent] = useState(!importId && (candidateIds || []).length === 1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const body = importId ? { import_id: importId } : { candidate_ids: candidateIds || [] };
  const bodyKey = JSON.stringify(body);

  useEffect(() => {
    authFetch('/api/enrich/estimate', { method: 'POST', headers: json, body: JSON.stringify({ ...JSON.parse(bodyKey), deep, include_recent: includeRecent }) })
      .then(setEst).catch(e => setError(e.message || 'Could not work out the cost.'));
  }, [authFetch, bodyKey, deep, includeRecent]);

  async function start() {
    setBusy(true); setError('');
    try {
      const r = await authFetch('/api/enrich', { method: 'POST', headers: json, body: JSON.stringify({ ...body, deep, include_recent: includeRecent }) });
      const bits = [plural(r.queued, 'person', 'people') + ' queued'];
      if (r.already_running) bits.push(r.already_running + ' already in progress');
      if (flash) flash('Enriching: ' + bits.join(', ') + '. Results land on their records as they finish.');
      if (onStarted) onStarted(r);
      onClose();
    } catch (e) { setError(e.message || 'Could not start.'); }
    setBusy(false);
  }

  const n = est ? est.count : null;
  const tooMany = est && est.count > est.max;
  return (
    <div className="gtc-modal-backdrop" onClick={() => !busy && onClose()}>
      <div className="gtc-modal gten-modal" onClick={e => e.stopPropagation()}>
        <div className="gtc-modal-head">{n == null ? 'Enrich' : 'Enrich ' + plural(n, 'person', 'people')}</div>
        <div className="gtc-modal-body">
          {error && <div className="gtnt-banner gtnt-banner--amber">{error}</div>}
          {!est ? <div className="gtr-empty-sm">Working out the cost…</div> : (
            <>
              <ul className="gten-steps">
                <li><b>Finds their public profile</b><span>LinkedIn, GitHub, publications and the web, plus their CV if we hold one.</span></li>
                <li><b>Checks it is really them</b><span>If we are not sure, nothing is written and the record asks you to look.</span></li>
                <li><b>Fills the profile</b><span>Empty fields are filled, each with its source and confidence. Anything you entered is never overwritten: differences come to you as suggestions.</span></li>
              </ul>
              <div className="gten-cost">
                <div><span>Estimated cost</span><b>{money(est.total_gbp)}</b></div>
                <div><span>Per person</span><b>about {money(est.per_person_gbp)}</b></div>
                <div><span>Contacts anyone</span><b>Never</b></div>
              </div>
              {est.recently_enriched > 0 && !deep && (est.selected > 1 || !includeRecent) && (
                <label className="gten-deep">
                  <input type="checkbox" className="gtpb-check" checked={includeRecent} onChange={e => setIncludeRecent(e.target.checked)} />
                  <span><b>Redo recent ones</b> {plural(est.recently_enriched, 'person was', 'people were')} enriched in the last {est.recent_days} days and will be {includeRecent ? 'looked up again.' : 'skipped to save cost. Tick to redo them.'}</span>
                </label>
              )}
              {tooMany && <div className="gtnt-banner gtnt-banner--amber">Up to {est.max} people at a time. Start with the ones that matter, from a bench or a role.</div>}
              {!est.keys_ready && <div className="gtnt-banner gtnt-banner--amber">The server is missing its Exa or Anthropic key, so runs will fail until it is set.</div>}
              {allowDeep && est.deep_allowed && (
                <label className="gten-deep">
                  <input type="checkbox" className="gtpb-check" checked={deep} onChange={e => setDeep(e.target.checked)} />
                  <span><b>Deep enrich</b> Also searches the live web to confirm and fill gaps. Slower and costs more. Best for a shortlisted person.</span>
                </label>
              )}
            </>
          )}
        </div>
        <div className="gtc-modal-foot">
          <button className="gtc-btn-ghost" disabled={busy} onClick={onClose}>Cancel</button>
          <button className="gtc-primary" disabled={busy || !est || !est.count || tooMany} onClick={start}>
            {busy ? 'Starting…' : !est ? 'Enrich' : !est.count ? 'Nobody to enrich' : 'Enrich ' + plural(est.count, 'person', 'people')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Follow runs to the end ───────────────────────────────────────────────
export function GtEnrichProgress({ runIds, authFetch, onFinished, onDismiss }) {
  const [runs, setRuns] = useState(null);
  const finishedOnce = useRef(false);
  const key = (runIds || []).join(',');

  useEffect(() => {
    finishedOnce.current = false;
    setRuns(null);
    if (!key) return undefined;
    let live = true;
    let timer = null;
    const tick = async () => {
      try {
        const r = await authFetch('/api/enrich/status?run_ids=' + key);
        if (!live) return;
        setRuns(r.runs || []);
        const open = (r.runs || []).some(x => x.status === 'queued' || x.status === 'running');
        if (open) timer = setTimeout(tick, 3000);
        else if (!finishedOnce.current) { finishedOnce.current = true; if (onFinished) onFinished(r.runs || []); }
      } catch (e) { if (live) timer = setTimeout(tick, 6000); }
    };
    tick();
    return () => { live = false; if (timer) clearTimeout(timer); };
  }, [authFetch, key, onFinished]);

  if (!key || !runs || !runs.length) return null;
  const total = runs.length;
  const count = s => runs.filter(x => x.status === s).length;
  const open = count('queued') + count('running');
  const done = count('done');
  const review = count('needs_review');
  const failed = count('failed');
  const pct = total ? Math.round(((total - open) / total) * 100) : 0;
  return (
    <div className={'gten-progress' + (open ? '' : ' gten-progress--done')} role="status">
      <div className="gten-progress-text">
        {open
          ? <><b>Enriching {plural(total, 'person', 'people')}</b><span>{total - open} of {total} finished. Results land on each record as they finish.</span></>
          : <><b>Enrichment finished</b><span>{[plural(done, 'updated', 'updated'), review ? plural(review, 'needs a look', 'need a look') : null, failed ? plural(failed, 'failed', 'failed') : null].filter(Boolean).join(' · ')}</span></>}
      </div>
      <div className="gten-progress-bar"><span style={{ width: Math.max(4, pct) + '%' }} /></div>
      {!open && onDismiss && <button type="button" className="gtc-btn-ghost" onClick={onDismiss}>Dismiss</button>}
    </div>
  );
}

// ── The record's card ────────────────────────────────────────────────────
export function GtEnrichCard({ candidateId, getToken, onChanged }) {
  const authFetch = useEnrichFetch(getToken);
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const flash = useCallback((msg, isErr) => { if (isErr) setErr(msg); }, []);
  const [modal, setModal] = useState(false);
  const [busy, setBusy] = useState(null);
  const [showSources, setShowSources] = useState(false);

  const load = useCallback(async () => {
    try { setData(await authFetch('/api/candidates/' + encodeURIComponent(candidateId) + '/enrichment')); }
    catch (e) { setData({ runs: [], error: e.message }); }
  }, [authFetch, candidateId]);
  useEffect(() => { load(); }, [load]);

  const latest = data && data.runs && data.runs[0];
  const working = latest && (latest.status === 'queued' || latest.status === 'running');
  useEffect(() => {
    if (!working) return undefined;
    const t = setTimeout(async () => {
      await load();
    }, 3000);
    return () => clearTimeout(t);
  }, [working, data, load]);
  const wasWorking = useRef(false);
  useEffect(() => {
    if (wasWorking.current && latest && !working && onChanged) onChanged();
    wasWorking.current = !!working;
  }, [working, latest, onChanged]);

  async function decide(run, s, action) {
    setBusy('s' + run.id + '-' + s.id); setErr('');
    try {
      await authFetch('/api/enrich/runs/' + run.id + '/suggestions/' + s.id, { method: 'POST', headers: json, body: JSON.stringify({ action }) });
      await load();
      if (action === 'accept' && onChanged) onChanged();
    } catch (e) { if (flash) flash(e.message, true); }
    setBusy(null);
  }
  async function confirm(run) {
    setBusy('c' + run.id); setErr('');
    try {
      await authFetch('/api/enrich/runs/' + run.id + '/confirm', { method: 'POST', headers: json, body: '{}' });
      await load();
      if (onChanged) onChanged();
    } catch (e) { if (flash) flash(e.message, true); }
    setBusy(null);
  }

  if (!data) return <div className="gtr-card gten-card"><div className="gtr-cardh">Enrichment</div><div className="gtr-empty-sm">Loading…</div></div>;

  const sources = latest ? (latest.sources || []).filter(s => s.url || s.kind === 'CV')
    .sort((a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind)) : [];
  const open = latest ? (latest.suggestions || []).filter(s => s.status === 'open') : [];
  const applied = latest && latest.applied ? latest.applied : {};
  const filled = (applied.fields || []).length + Object.values(applied.lists || {}).reduce((t, n) => t + n, 0);

  return (
    <div className="gtr-card gten-card">
      <div className="gtr-cardh">
        Enrichment
        {!working && <button className="gtr-cardh-edit" onClick={() => setModal(true)}>{latest ? 'Enrich again' : 'Enrich'}</button>}
      </div>

      {err && <div className="gten-state gten-state--bad"><span>{err}</span></div>}
      {!latest && <p className="gten-muted">Fill this profile from their public footprint and CV, with a source on every item. Nothing is sent to them.</p>}

      {working && (
        <div className="gten-state gten-state--run"><span className="gten-spin" aria-hidden="true" />{latest.mode === 'deep' ? 'Deep enrich in progress…' : 'Enriching…'}</div>
      )}

      {latest && latest.status === 'done' && (
        <div className="gten-state gten-state--ok">
          <b>Enriched {ukDate(latest.finished_at)}</b>
          <span>{filled ? plural(filled, 'item', 'items') + ' filled' : 'Nothing new to fill'}{open.length ? ' · ' + plural(open.length, 'suggestion', 'suggestions') + ' below' : ''}</span>
        </div>
      )}

      {latest && latest.status === 'needs_review' && (
        <div className="gten-state gten-state--warn">
          <b>Not sure this is them</b>
          <span>{latest.identity_reason || 'The sources may be about someone else.'}</span>
          {latest.has_result && (
            <button className="gtc-btn-ghost gten-confirm" disabled={busy === 'c' + latest.id} onClick={() => confirm(latest)}>
              {busy === 'c' + latest.id ? 'Applying…' : 'I have checked: it is them'}
            </button>
          )}
        </div>
      )}

      {latest && latest.status === 'failed' && (
        <div className="gten-state gten-state--bad"><b>Enrichment failed</b><span>{latest.error || 'Try again in a moment.'}</span></div>
      )}

      {sources.length > 0 && (
        <div className="gten-sources">
          <button type="button" className="gten-linkbtn" onClick={() => setShowSources(v => !v)}>
            {showSources ? 'Hide sources' : plural(sources.length, 'source', 'sources')}
          </button>
          {showSources && (
            <ul>
              {sources.map(s => (
                <li key={s.n}>
                  <span className={'gten-kind gten-kind--' + String(s.kind).toLowerCase()}>{s.kind}</span>
                  {s.url ? <a href={s.url} target="_blank" rel="noreferrer" title={s.url}>{s.title || s.url.replace(/^https?:\/\/(www\.)?/, '')}</a> : <span>{s.title || 'Their CV'}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {open.length > 0 && (
        <div className="gten-sugs">
          <div className="gten-sugs-h">Suggestions</div>
          {open.map(s => (
            <div key={s.id} className="gten-sug">
              <div className="gten-sug-what">
                <span className="gten-sug-label">{s.label}</span>
                {s.kind === 'field' ? (
                  <span className="gten-sug-val"><s>{String(s.current)}</s> <b>{String(s.value)}</b></span>
                ) : (
                  <span className="gten-sug-val"><b>Add: {s.text}</b></span>
                )}
                <span className="gten-sug-src">{s.url ? <a href={s.url} target="_blank" rel="noreferrer">{s.source}</a> : s.source} · {Math.round((s.confidence || 0) * 100)}% sure</span>
              </div>
              <div className="gten-sug-acts">
                <button className="gtc-btn-ghost" disabled={!!busy} onClick={() => decide(latest, s, 'dismiss')}>Dismiss</button>
                <button className="gtc-primary" disabled={!!busy} onClick={() => decide(latest, s, 'accept')}>{busy === 's' + latest.id + '-' + s.id ? 'Saving…' : 'Accept'}</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <GtEnrichModal candidateIds={[data.candidate_id]} authFetch={authFetch} flash={flash}
          onClose={() => setModal(false)} onStarted={() => load()} />
      )}
    </div>
  );
}
